import { useTranslation } from 'react-i18next';
import { Monitor, Moon, Sun } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useTheme, type Theme } from '@/components/theme-provider';
import { cn } from '@/lib/utils';

/**
 * Light / dark / follow-the-system.
 *
 * The choice is persisted per browser by `ThemeProvider` under a key namespaced
 * with `VITE_PREFIX`, so it survives a reload and never leaks between clients
 * sharing a host. It reads on first paint, before React renders, so there is no
 * flash of the wrong theme.
 *
 * This control exists in three places, all driven by the same store: here in
 * the signed-in header, in the avatar menu, and on the signed-out auth screens
 * — where there is no avatar menu to hide it in and someone working at night
 * should not have to sign in first to turn the lights down.
 */
export function ThemeToggle({ className }: { className?: string }) {
  const { theme, resolvedTheme, setTheme } = useTheme();
  const { t } = useTranslation();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={t('shell.appearance')}
        className={cn(
          'text-muted-foreground hover:bg-accent hover:text-accent-foreground focus-visible:ring-ring/50',
          'inline-flex size-9 items-center justify-center rounded-md transition-colors outline-none focus-visible:ring-2',
          className
        )}
      >
        {/* The icon shows what you are looking at, not what the setting says —
            "System" has no icon of its own that means anything to a viewer. */}
        {resolvedTheme === 'dark' ? (
          <Moon className="size-4" />
        ) : (
          <Sun className="size-4" />
        )}
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-40">
        <DropdownMenuRadioGroup
          value={theme}
          onValueChange={(value) => setTheme(value as Theme)}
        >
          <DropdownMenuRadioItem value="light">
            <Sun />
            {t('shell.themeLight')}
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="dark">
            <Moon />
            {t('shell.themeDark')}
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="system">
            <Monitor />
            {t('shell.themeSystem')}
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
